import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  ScrollArea,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@zond/ui';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  useDeleteAllWebhooks,
  useDeleteWebhook,
  useExportProjects,
  useExportUsers,
  useGetIntegrationProjects,
  useGetIntegrationUsers,
  useGetIntegrationWebhooks,
} from '../integrations.queries';
import { ConnectionAlerts } from './integration-settings-dialog/connection-alerts';
import { DialogHeader } from './integration-settings-dialog/dialog-header';
import { getIntegrationSettings, hasIntegrationSettings } from './integration-settings-dialog/integrations';
import { LoadingSkeleton } from './integration-settings-dialog/loading-skeleton';
import { ProjectsTab } from './integration-settings-dialog/projects-tab';
import { IntegrationSettingsDialogProps } from './integration-settings-dialog/types';
import { UsersTab } from './integration-settings-dialog/users-tab';
import { getProjectId, getUserId, isGithub } from './integration-settings-dialog/utils';
import { WebhooksTab } from './integration-settings-dialog/webhooks-tab';

export function IntegrationSettingsDialog({
  open,
  onOpenChange,
  integrationName,
  logoUrl,
}: IntegrationSettingsDialogProps) {
  const { t } = useTranslation('integrations');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const isGithubIntegration = isGithub(integrationName);
  const hasCustomSettings = hasIntegrationSettings(integrationName);
  const IntegrationSettingsComponent = hasCustomSettings ? getIntegrationSettings(integrationName) : null;
  const isDevelopment = import.meta.env.MODE === 'development';

  const { data: users, isLoading: usersLoading } = useGetIntegrationUsers(integrationName, open);
  const { data: projects, isLoading: projectsLoading } = useGetIntegrationProjects(integrationName, open);
  const { data: webhooks, isLoading: webhooksLoading } = useGetIntegrationWebhooks(
    integrationName,
    open && isDevelopment
  );

  const { mutate: exportProjects, isPending: isExportingProjects } = useExportProjects(
    integrationName,
    (data) => {
      const { created, existing } = data;
      if (created.length > 0) {
        toast.success(
          existing.length > 0
            ? t('toasts.projectsExportedWithExisting', { count: created.length, existing: existing.length })
            : t('toasts.projectsExported', { count: created.length })
        );
      } else if (existing.length > 0) {
        toast.info(t('toasts.allProjectsExist', { count: existing.length }));
      }
      setSelectedProjects(new Set());
    },
    () => toast.error(t('toasts.projectsExportFailed'))
  );

  const { mutate: exportUsers, isPending: isExportingUsers } = useExportUsers(
    integrationName,
    (data) => {
      const { invited, existing } = data;
      if (invited.length > 0) {
        toast.success(
          existing.length > 0
            ? t('toasts.usersInvitedWithExisting', { count: invited.length, existing: existing.length })
            : t('toasts.usersInvited', { count: invited.length })
        );
      } else if (existing.length > 0) {
        toast.info(t('toasts.allUsersExist', { count: existing.length }));
      }
      setSelectedUsers(new Set());
    },
    () => toast.error(t('toasts.usersExportFailed'))
  );

  const { mutate: deleteWebhook, isPending: isDeletingWebhook } = useDeleteWebhook(
    integrationName,
    () => toast.success(t('toasts.webhookDeleted')),
    () => toast.error(t('toasts.webhookDeleteFailed'))
  );

  const { mutate: deleteAllWebhooks, isPending: isDeletingAllWebhooks } = useDeleteAllWebhooks(
    integrationName,
    () => toast.success(t('toasts.allWebhooksDeleted')),
    () => toast.error(t('toasts.allWebhooksDeleteFailed'))
  );

  const handleUserToggle = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleProjectToggle = (projectId: string) => {
    const newSelected = new Set(selectedProjects);
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId);
    } else {
      newSelected.add(projectId);
    }
    setSelectedProjects(newSelected);
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.size === users?.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users?.map((u: any) => getUserId(u)) || []));
    }
  };

  const handleSelectAllProjects = () => {
    if (selectedProjects.size === projects?.length) {
      setSelectedProjects(new Set());
    } else {
      setSelectedProjects(new Set(projects?.map((p: any) => getProjectId(p)) || []));
    }
  };

  const handleExportUsers = () => {
    if (selectedUsers.size === 0) {
      toast.error(t('toasts.selectUserToExport'));
      return;
    }
    exportUsers(Array.from(selectedUsers));
  };

  const handleExportProjects = () => {
    if (selectedProjects.size === 0) {
      toast.error(t('toasts.selectProjectToExport'));
      return;
    }
    exportProjects(Array.from(selectedProjects));
  };

  const handleDisconnectSuccess = () => {
    onOpenChange(false);
  };

  const handleDeleteWebhook = (webhookKey: string) => {
    deleteWebhook(webhookKey);
  };

  const handleDeleteAllWebhooks = () => {
    deleteAllWebhooks();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[800px] !max-w-[800px] h-[85vh] flex flex-col p-0">
        <DialogHeader integrationName={integrationName} logoUrl={logoUrl} />

        <Separator />

        <ScrollArea className="flex-1 min-h-0">
          <Tabs defaultValue="users" className="flex flex-col overflow-x-hidden">
            <div className="px-6 pt-4">
              <TabsList
                className={`grid w-full ${isDevelopment ? (hasCustomSettings ? 'grid-cols-4' : 'grid-cols-3') : hasCustomSettings ? 'grid-cols-3' : 'grid-cols-2'}`}
              >
                <TabsTrigger value="users" className="flex items-center gap-2">
                  {isGithubIntegration ? t('tabs.collaborators') : t('tabs.users')}
                </TabsTrigger>
                <TabsTrigger value="projects" className="flex items-center gap-2">
                  {isGithubIntegration ? t('tabs.repositories') : t('tabs.projects')}
                </TabsTrigger>
                {hasCustomSettings && (
                  <TabsTrigger value="integration-settings" className="flex items-center gap-2">
                    {t('tabs.settings')}
                  </TabsTrigger>
                )}
                {isDevelopment && (
                  <TabsTrigger value="webhooks" className="flex items-center gap-2">
                    {t('tabs.webhooks')}
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            <UsersTab
              integrationName={integrationName}
              users={users}
              usersLoading={usersLoading}
              selectedUsers={selectedUsers}
              onUserToggle={handleUserToggle}
              onSelectAllUsers={handleSelectAllUsers}
              onExportUsers={handleExportUsers}
              isExportingUsers={isExportingUsers}
            />

            <ProjectsTab
              integrationName={integrationName}
              projects={projects}
              projectsLoading={projectsLoading}
              selectedProjects={selectedProjects}
              onProjectToggle={handleProjectToggle}
              onSelectAllProjects={handleSelectAllProjects}
              onExportProjects={handleExportProjects}
              isExportingProjects={isExportingProjects}
            />

            {hasCustomSettings && IntegrationSettingsComponent && (
              <TabsContent value="integration-settings" className="flex-1">
                <Suspense fallback={<LoadingSkeleton />}>
                  <IntegrationSettingsComponent integrationName={integrationName} />
                </Suspense>
              </TabsContent>
            )}

            {isDevelopment && (
              <WebhooksTab
                integrationName={integrationName}
                webhooks={webhooks || []}
                webhooksLoading={webhooksLoading}
                onDeleteWebhook={handleDeleteWebhook}
                isDeletingWebhook={isDeletingWebhook}
                onDeleteAllWebhooks={handleDeleteAllWebhooks}
                isDeletingAllWebhooks={isDeletingAllWebhooks}
              />
            )}

            <ConnectionAlerts
              integrationName={integrationName}
              onDisconnectSuccess={handleDisconnectSuccess}
            />
          </Tabs>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
